import { Inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Analyst, FindingVersionRef, RoiFindingProjectionService } from '@analysis/domain';
import { GetSampleStatsUseCase, GetProjectionStatusUseCase } from '@analysis/use-cases';
import { EMPTY, from, of, timer, Observable, Subject } from 'rxjs';
import { catchError, concatMap, filter, last, map, scan, switchMap, takeWhile, tap } from 'rxjs/operators';
import {
  RoiStore,
  AssetStore,
  RoiStatsStore,
  CounterExclusionMode,
  MosaicPaginationStore,
} from '../../stores';
import { ProtocolStore } from '@telespot/protocols/ui';

export const COUNTER_EXCLUDED_STORAGE_KEY_PREFIX = 'telespot_counterExcludedLabels';
export const COUNTER_EXCLUSION_MODE_STORAGE_KEY_PREFIX = 'telespot_counterExclusionMode';
export const GET_SAMPLE_STATS_USE_CASE = new InjectionToken<GetSampleStatsUseCase>('GET_SAMPLE_STATS_USE_CASE');
export const GET_PROJECTION_STATUS_USE_CASE = new InjectionToken<GetProjectionStatusUseCase>(
  'GET_PROJECTION_STATUS_USE_CASE'
);

export function joinStorageKey(prefix: string, methodTypeId: string): string {
  return `${prefix}_${methodTypeId}`;
}

/**
 * What one watermark read concluded.
 *
 * `unknown` is a read that failed: evidence of nothing. Calling it stale would lock the view
 * on a network blip; calling it current would drop a banner that still applies.
 */
interface ProjectionMeasurement {
  outcome: 'current' | 'stale' | 'unknown';
  /** Findings the projection is holding, which is what the stall budget watches. */
  processed: number;
}

/** A stats read that has been applied to the stores, and which generation it was for. */
export interface SampleStatsApplied {
  sampleId: string;
  analystId: string;
  /** False for a refresh of the (sample, analyst) already on screen. */
  isNewGeneration: boolean;
}

@Injectable()
export class RoiStatsEffects {
  constructor(
    private readonly roiStore: RoiStore,
    private readonly assetStore: AssetStore,
    private readonly roiStatsStore: RoiStatsStore,
    private readonly paginationStore: MosaicPaginationStore,
    private readonly protocolStore: ProtocolStore,
    @Inject(GET_SAMPLE_STATS_USE_CASE) private readonly getSampleStatsUseCase: GetSampleStatsUseCase,
    @Inject(GET_PROJECTION_STATUS_USE_CASE) private readonly getProjectionStatusUseCase: GetProjectionStatusUseCase
  ) { }

  private readonly PROJECTION_POLL_INTERVAL_MS = 3000;
  /**
   * Ticks without the processed count rising before the poll gives up. A projection still
   * mirroring findings keeps its budget however long the sample takes; only a stalled one
   * runs out, which is what the banner reports as needing a refresh.
   */
  private readonly PROJECTION_MAX_STALLED_POLLS = 20;

  readonly checkProjection = this.roiStore.effect((trigger$: Observable<{ sampleId: string; analyst: Analyst }>) =>
    trigger$.pipe(
      switchMap(({ sampleId, analyst }) => {
        this.roiStatsStore.setProjectionPollingExhausted(false);

        return timer(0, this.PROJECTION_POLL_INTERVAL_MS).pipe(
          concatLatestFrom(() => [this.roiStatsStore.ownSaveVersions$, this.roiStatsStore.expectedVersions$]),
          concatMap(([, ownSaves, sourceOfRecord]) =>
            this.measureProjection(sampleId, analyst.id, ownSaves, sourceOfRecord)
          ),
          scan<ProjectionMeasurement, ProjectionMeasurement & { stalledTicks: number }>(
            (previous, measurement) => ({
              ...measurement,
              stalledTicks: measurement.processed > previous.processed ? 0 : previous.stalledTicks + 1,
            }),
            // -1 processed so the first tick always counts as an advance.
            { outcome: 'current', processed: -1, stalledTicks: 0 }
          ),
          // Keep polling while the view is stale and still advancing; emit whatever ends it.
          takeWhile(
            ({ outcome, stalledTicks }) => outcome === 'stale' && stalledTicks < this.PROJECTION_MAX_STALLED_POLLS,
            true
          ),
          // Isolates the emission that ENDED the poll, so what counts as giving up is read
          // off the stream rather than re-derived from the budget here.
          last(),
          tap(({ outcome }) => {
            // Either the view is current, or we can no longer tell the analyst when it will
            // be: a stalled projection and a failed read alike leave the banner latched with
            // nothing further coming, and both have to offer the refresh.
            if (outcome !== 'current') this.roiStatsStore.setProjectionPollingExhausted(true);
          })
        );
      })
    )
  );

  /** One watermark read, stored and reduced to what the poll loop needs to decide. */
  private measureProjection(
    sampleId: string,
    creatorId: string,
    ownSaves: Map<string, FindingVersionRef>,
    sourceOfRecord: Map<string, FindingVersionRef>
  ): Observable<ProjectionMeasurement> {
    // Nothing the view could be waiting for - findings we saved ourselves included, since
    // the store already holds their rows. Still recorded as measured, so the banner
    // resolves rather than sitting on a stale "not yet measured".
    if (sourceOfRecord.size === 0) {
      this.roiStatsStore.setProjectionStatus({ projectedVersions: new Map(), complete: false });
      return of<ProjectionMeasurement>({ outcome: 'current', processed: 0 });
    }

    return from(this.getProjectionStatusUseCase.execute({ sampleId, creatorId })).pipe(
      tap(({ projectedVersions, complete }) => this.roiStatsStore.setProjectionStatus({ projectedVersions, complete })),
      map(({ projectedVersions, complete }): ProjectionMeasurement => {
        const progress = RoiFindingProjectionService.progress(
          { ownSaves, sourceOfRecord, complete },
          projectedVersions
        );

        return {
          outcome: RoiFindingProjectionService.isProgressComplete(progress) ? 'current' : 'stale',
          processed: progress.processed,
        };
      }),
      catchError(() => of<ProjectionMeasurement>({ outcome: 'unknown', processed: 0 }))
    );
  }

  public readonly sampleCategoryCounts$ = this.roiStatsStore.select(
    this.roiStatsStore.counterExcludedLabels$,
    this.roiStatsStore.activeLabelCounts$,
    this.protocolStore.categories$,
    (counterExcludedLabels, labelCounts, categories) => {
      const categoryCounts: Map<string, number> = new Map();

      for (const { id, options } of Object.values(categories)) {
        let count = 0;

        for (const option of options) {
          if (counterExcludedLabels.has(option)) continue;
          count += labelCounts.get(option) ?? 0;
        }

        categoryCounts.set(id, count);
      }

      return categoryCounts;
    }
  );

  private readonly activeAssetRoiStats$ = this.roiStore.select(
    this.assetStore.selectedAsset$,
    this.roiStore.assetIndex$,
    this.roiStore.rois$,
    this.roiStatsStore.counterExcludedLabels$,
    this.roiStatsStore.counterExclusionMode$,
    (selectedAsset, assetIndex, rois, counterExcludedLabels, counterExclusionMode) => {
      if (!selectedAsset?.id || !rois) return { countsByLabel: new Map<string, number>(), totalCount: 0 };

      const activeAssetRoiIds = assetIndex.get(selectedAsset.id) || new Set<string>();
      const countsByLabel = new Map<string, number>();
      let totalCount = 0;

      const isMultiActiveComplete = (labels: string[]) => !labels.some((l) => counterExcludedLabels.has(l));
      const isMultiActiveSelective = (labels: string[]) => !labels.every((l) => counterExcludedLabels.has(l));

      activeAssetRoiIds.forEach((roiId) => {
        const roi = rois[roiId];
        if (!roi?.labels?.size) return;

        const labels = Array.from(roi.labels.keys());
        if (labels.length === 1) {
          if (!counterExcludedLabels.has(labels[0])) {
            countsByLabel.set(labels[0], (countsByLabel.get(labels[0]) ?? 0) + 1);
            totalCount++;
          }
        } else {
          const isActive =
            counterExclusionMode === 'complete' ? isMultiActiveComplete(labels) : isMultiActiveSelective(labels);
          if (isActive) {
            labels.forEach((labelId) => {
              countsByLabel.set(labelId, (countsByLabel.get(labelId) ?? 0) + 1);
            });
            totalCount++;
          }
        }
      });

      return { countsByLabel, totalCount };
    }
  );

  public readonly activeAssetRoiCountsByLabel$ = this.roiStore.select(
    this.activeAssetRoiStats$,
    (stats) => stats.countsByLabel
  );

  public readonly totalActiveAssetRoiCount$ = this.roiStore.select(
    this.activeAssetRoiStats$,
    (stats) => stats.totalCount
  );

  public readonly assetCategoryCounts$ = this.roiStatsStore.select(
    this.roiStatsStore.counterExcludedLabels$,
    this.activeAssetRoiCountsByLabel$,
    this.protocolStore.categories$,
    (counterExcludedLabels, labelCounts, categories) => {
      const categoryCounts: Map<string, number> = new Map();

      for (const { id, options } of Object.values(categories)) {
        let count = 0;

        for (const option of options) {
          if (counterExcludedLabels.has(option)) continue;
          count += labelCounts.get(option) ?? 0;
        }

        categoryCounts.set(id, count);
      }

      return categoryCounts;
    }
  );

  /**
   * Emits the (sample, analyst) whose stats were just applied, so consumers react to the
   * generation the numbers actually belong to instead of pairing a bare "stats finished"
   * edge with whatever is current by then.
   */
  private readonly statsAppliedSubject = new Subject<SampleStatsApplied>();
  public readonly statsApplied$ = this.statsAppliedSubject.asObservable();

  /** The (sample, analyst) the grid is currently initialised for. */
  private appliedGeneration: string | undefined;

  readonly getSampleStats = this.roiStore.effect((action$: Observable<{ sampleId: string; analyst: Analyst }>) =>
    action$.pipe(
      concatLatestFrom(() => [this.protocolStore.options$]),
      // switchMap, NOT concatMap: a request for a superseded (sample, analyst) must be
      // cancelled, not queued behind — its result would otherwise still be applied, wiping
      // the grid and painting the previous generation's counters.
      switchMap(([{ sampleId, analyst }, options]) => {
        const request = {
          sampleId,
          analystEntity: analyst.type,
          analystId: analyst.id,
        };

        this.roiStatsStore.setLoading(true);

        return from(this.getSampleStatsUseCase.execute(request)).pipe(
          tap((results) => {
            const newLabelCounts = new Map<string, number>();

            for (const [id, count] of results.labelCounts) newLabelCounts.set(id, count);

            const labelIds = Object.keys(options);

            this.roiStatsStore.setSampleStats({
              labelCounts: newLabelCounts,
              singleLabelCount: results.singleLabelCount ?? new Map(),
              multiLabelCount: results.multiLabelCount ?? new Map(),
              findingVersions: results.findingVersions,
            });

            const generation = `${sampleId}:${analyst.id}`;
            const isNewGeneration = generation !== this.appliedGeneration;
            this.appliedGeneration = generation;

            if (isNewGeneration) {
              this.roiStore.initializeLabelIndex(labelIds);
              this.paginationStore.initializeProgress({ labelIds, labelCounts: newLabelCounts });
              this.roiStatsStore.resetForNewGeneration();
            }

            this.statsAppliedSubject.next({ sampleId, analystId: analyst.id, isNewGeneration });
          }),
          catchError((error) => {
            this.roiStatsStore.setLoadError(error.message);
            return EMPTY;
          })
        );
      })
    )
  );

  readonly getCounterExclusionConfig = this.roiStore.effect((methodTypeId$: Observable<string>) =>
    methodTypeId$.pipe(
      // TODO: convert to use case
      tap((methodTypeId) => {
        try {
          const modeKey = joinStorageKey(COUNTER_EXCLUSION_MODE_STORAGE_KEY_PREFIX, methodTypeId);
          const excludedKey = joinStorageKey(COUNTER_EXCLUDED_STORAGE_KEY_PREFIX, methodTypeId);

          const mode = localStorage.getItem(modeKey);
          const excluded = localStorage.getItem(excludedKey);

          const config: { mode?: CounterExclusionMode; excludedLabels?: Set<string> } = {};
          if (mode) config.mode = mode as CounterExclusionMode;
          if (excluded) config.excludedLabels = new Set(JSON.parse(excluded) as string[]);
          this.roiStatsStore.setCounterExclusionConfig(config);
        } catch {
          // Invalid or corrupted stored value; ignore and keep default exclusions.
          console.error('Invalid or corrupted stored value for counter exclusion config');
        }
      }),
      switchMap(() => EMPTY)
    )
  );

  readonly toggleCounterExclusionMode = this.roiStore.effect((mode$: Observable<CounterExclusionMode>) =>
    mode$.pipe(
      concatLatestFrom(() => [this.protocolStore.methodTypeId$]),
      // TODO: convert to use case
      tap(([mode, methodTypeId]) => {
        const modeKey = joinStorageKey(COUNTER_EXCLUSION_MODE_STORAGE_KEY_PREFIX, methodTypeId);
        localStorage.setItem(modeKey, mode);
        this.roiStatsStore.setCounterExclusionConfig({ mode });
      }),
      switchMap(() => EMPTY)
    )
  );

  readonly toggleCounterExcluded = this.roiStore.effect((labelId$: Observable<string>) =>
    labelId$.pipe(
      concatLatestFrom(() => [this.protocolStore.methodTypeId$, this.roiStatsStore.counterExcludedLabels$]),
      filter(([, methodTypeId]) => !!methodTypeId),
      // TODO: convert to use case
      tap(([labelId, methodTypeId, excluded]) => {
        const updated = new Set(excluded);
        updated.has(labelId) ? updated.delete(labelId) : updated.add(labelId);
        this.roiStatsStore.setCounterExclusionConfig({ excludedLabels: updated });
        const exclusionKey = joinStorageKey(COUNTER_EXCLUDED_STORAGE_KEY_PREFIX, methodTypeId);
        localStorage.setItem(exclusionKey, JSON.stringify(Array.from(updated)));
      }),
      switchMap(() => EMPTY)
    )
  );
}
